import { TwoUIBiCustomClick, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { OmitStrict } from "@amx/ux-twoui-utils";
import React from "react";
import { ActionButton, ActionButtonProps } from "../../../../Buttons/ActionButton/ActionButton";
import { MenuIconButtonProps } from "../../../../Buttons/MenuIconButton/MenuIconButton";
import { FlexibleMenu, FlexibleMenuProps } from "../../../../FlexibleMenu/FlexibleMenu";
import { Stack } from "../../../../Stacks/Stack/Stack";
import { StopPropagation } from "../../../../StopPropagation/StopPropagation";
import { gapBetweenElements } from "../../../ListItems/shared/styles";
import { OperationalContent } from "../../shared/OperationalContent";
import { FinalColumnProps } from "../shared/props";
import { getFinalColumnContent } from "../shared/utils";

export interface BaseActionButtonFinalColumnProps {
    /**
     * ActionButtonProps to render ActionButton components at the far right of the column (LTR).
     */
    readonly actionButtonProps: readonly ActionButtonProps[];

    /**
     * Optional menu props already passed to the list item. These are needed if mixing with overflowed actions.
     */
    readonly menuProps?: OmitStrict<MenuIconButtonProps, "iconName">;

    /**
     * Optionally disable the contextual/overflow menu
     */
    readonly menuDisabled?: boolean;

    /**
     * If enabled, the text of ActionButtons that are not overflowed will be collapsed,
     * rendering only the icon.
     */
    readonly collapseActionText?: boolean;
}

export type ActionButtonFinalColumnProps = FinalColumnProps & BaseActionButtonFinalColumnProps;

export const ActionButtonFinalColumn: React.FC<ActionButtonFinalColumnProps> = ({
    actionButtonProps,
    menuProps = {
        "aria-label": "Menu Icon Button",
        "data-bi-id": "Menu Icon Button",
        tooltipHostProps: {
            tooltipText: ""
        },
        menuItems: []
    },
    collapseActionText,
    disabled,
    menuDisabled,
    operationalData,
    ...otherProps
}) => {
    const twoUITelemetry = useTwoUITelemetry();
    const pageArea = twoUITelemetry.twoUITelemetryIds.areaBiId;

    const finalColumnActions = actionButtonProps.map((action, index) => {
        const addReportClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            // create the custom click event
            const clickEvent: TwoUIBiCustomClick = {
                clickId: `${action["data-bi-id"]}`,
                pageArea: pageArea
            };
            // send the event
            twoUITelemetry.reportClick(clickEvent);

            // run the original onClick function
            if ("onClick" in action) {
                action.onClick(event);
            }
        };

        return (
            <StopPropagation key={index}>
                <ActionButton {...action} onClick={addReportClick} />
            </StopPropagation>
        );
    });

    const flexibleMenuProps: FlexibleMenuProps = {
        menuProps: menuProps,
        height: "40px",
        alignment: "flex-end",
        "aria-label": "",
        "data-bi-id": ""
    };

    return (
        <Stack horizontal={true} grow={true} childrenGap={gapBetweenElements}>
            <Stack verticalAlign="center">
                <OperationalContent
                    operationalData={operationalData}
                >
                    {getFinalColumnContent(otherProps, disabled)}
                </OperationalContent>
            </Stack>

            <FlexibleMenu {...flexibleMenuProps}>
                {finalColumnActions}
            </FlexibleMenu>
        </Stack>
    );
};
